/**
 * Static manifest for the Creative Review pipeline.
 *
 * Provides labels, descriptions, icons, and ordering for phases and stages
 * without requiring a backend round-trip. The manifest is used by UI
 * components to render stage chips, tooltips, and detail panels.
 *
 * Mirrors the JSON manifest in docs/creative-review/PIPELINE_TAXONOMY.md
 * and the PIPELINE_MANIFEST constant in app/creative_review/pipeline_state.py.
 */

import type { PhaseId, StageId } from '@/services/api';

export interface StageManifestEntry {
  stage_id: StageId;
  label: string;
  phase_id: PhaseId;
  icon: string;
  description: string;
  required: boolean;
  substeps?: { substep_id: string; label: string }[];
}

export interface PhaseManifestEntry {
  phase_id: PhaseId;
  label: string;
  order: number;
  stages: StageId[];
}

export const PHASE_MANIFEST: PhaseManifestEntry[] = [
  {
    phase_id: 'prepare',
    label: 'Prepare',
    order: 0,
    stages: ['resolve_context'],
  },
  {
    phase_id: 'understand',
    label: 'Understand',
    order: 1,
    stages: ['domain_lenses', 'scene_graph', 'visual_attention'],
  },
  {
    phase_id: 'review',
    label: 'Review',
    order: 2,
    stages: ['persona_review', 'derive_bindings', 'predictive_impact', 'persist_review'],
  },
  {
    phase_id: 'enrich',
    label: 'Enrich',
    order: 3,
    stages: ['upsert_pdo_concepts', 'ad_taxonomy', 'market_concepts', 'visual_template'],
  },
  {
    phase_id: 'finalize',
    label: 'Finalize',
    order: 4,
    stages: ['stitch_creative_meaning', 'persist_creative'],
  },
];

export const STAGE_MANIFEST: StageManifestEntry[] = [
  {
    stage_id: 'resolve_context',
    label: 'Resolve context',
    phase_id: 'prepare',
    icon: 'search',
    description: 'Loads the persona brief and resolves the creative asset URL.',
    required: true,
  },
  {
    stage_id: 'domain_lenses',
    label: 'Domain lenses',
    phase_id: 'understand',
    icon: 'layers',
    description: 'Runs domain-specific analysis lenses to build grounding context.',
    required: false,
  },
  {
    stage_id: 'scene_graph',
    label: 'Scene graph',
    phase_id: 'understand',
    icon: 'grid',
    description: 'Extracts structured scene graph elements (objects, layout, composition).',
    required: false,
  },
  {
    stage_id: 'visual_attention',
    label: 'Visual attention',
    phase_id: 'understand',
    icon: 'eye',
    description: 'Runs HAT saliency model to map visual attention regions.',
    required: false,
  },
  {
    stage_id: 'persona_review',
    label: 'Persona review',
    phase_id: 'review',
    icon: 'brain',
    description: 'Simulates how the target persona experiences the creative moment by moment.',
    required: true,
    substeps: [
      { substep_id: 'load_persona_brief', label: 'Load persona brief' },
      { substep_id: 'cognitive_walkthrough', label: 'Cognitive walkthrough' },
      { substep_id: 'persona_analysis', label: 'Persona analysis' },
      { substep_id: 'score', label: 'Scores & verdict' },
      { substep_id: 'recommendations', label: 'Recommendations' },
      { substep_id: 'chain_suggestions', label: 'Next actions' },
    ],
  },
  {
    stage_id: 'derive_bindings',
    label: 'Ontology bindings',
    phase_id: 'review',
    icon: 'link',
    description: 'Maps review insights to PDO ontology concepts.',
    required: true,
  },
  {
    stage_id: 'predictive_impact',
    label: 'Predictive impact',
    phase_id: 'review',
    icon: 'trending-up',
    description: 'Computes causal lift scores and ranked brief modifications.',
    required: false,
  },
  {
    stage_id: 'persist_review',
    label: 'Persist review',
    phase_id: 'review',
    icon: 'save',
    description: 'Writes the creative analysis and persona fit records to Core.',
    required: true,
  },
  {
    stage_id: 'upsert_pdo_concepts',
    label: 'PDO concepts',
    phase_id: 'enrich',
    icon: 'share-2',
    description: 'Upserts review binding concepts into the knowledge graph.',
    required: false,
  },
  {
    stage_id: 'ad_taxonomy',
    label: 'Ad taxonomy',
    phase_id: 'enrich',
    icon: 'tag',
    description: 'Extracts hook, angle, proof, and format taxonomy elements.',
    required: false,
  },
  {
    stage_id: 'market_concepts',
    label: 'Market concepts',
    phase_id: 'enrich',
    icon: 'bar-chart-2',
    description: 'Emits consumer cognitive mechanics market concepts.',
    required: false,
  },
  {
    stage_id: 'visual_template',
    label: 'Visual template',
    phase_id: 'enrich',
    icon: 'layout',
    description: 'Classifies creative template, layout archetype, and visual style.',
    required: false,
  },
  {
    stage_id: 'stitch_creative_meaning',
    label: 'Stitch meaning',
    phase_id: 'finalize',
    icon: 'edit-3',
    description: 'Stitches hook, emotion, and concept IDs into Creative.meaning.',
    required: true,
  },
  {
    stage_id: 'persist_creative',
    label: 'Persist creative',
    phase_id: 'finalize',
    icon: 'database',
    description: 'Persists the enriched creative record to the data store.',
    required: true,
  },
];

/** Fast lookup by stage_id */
export const STAGE_MAP = Object.fromEntries(
  STAGE_MANIFEST.map((s) => [s.stage_id, s]),
) as Record<StageId, StageManifestEntry>;

/** Fast lookup by phase_id */
export const PHASE_MAP = Object.fromEntries(
  PHASE_MANIFEST.map((p) => [p.phase_id, p]),
) as Record<PhaseId, PhaseManifestEntry>;
